import React, { ReactNode, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HintIcon } from '../../icons/hint-icon'
import { TippyIcon } from './icons/tippy-icon'
import { TooltipArrowUi } from './ui/tooltip-arrow-ui'
import { TooltipButtonUi } from './ui/tooltip-button-ui'
import {
  TooltipMessageUi,
  TooltipMessageUiProps,
} from './ui/tooltip-message-ui'
import { TooltipOverlayUi } from './ui/tooltip-overlay-ui'
import { TooltipWrapperUi } from './ui/tooltip-wrapper-ui'

interface TooltipProps {
  children?: ReactNode
  message: string
  isArrowCenter?: boolean
  skipTranslation?: boolean
  size?: 'small' | 'medium' | 'large' | number
  messageColor?: string
  buttonColor?: string
  buttonColorActive?: string
  btnStyles?: React.CSSProperties
}

interface TooltipStateInterface extends TooltipMessageUiProps {
  arrowLeft: string
}

const defaultMessageMaxWidth = 350
const defaultMinLeft = 7

export const Tooltip = ({
  children,
  message,
  isArrowCenter,
  skipTranslation = false,
  size = 'small',
  messageColor = '#19191B',
  buttonColor = '#E4E5E7',
  buttonColorActive = '#00A0FF',
  btnStyles,
}: TooltipProps) => {
  const { t } = useTranslation()
  const tooltipIcon = useRef<HTMLButtonElement>(null)
  const tooltipMessage = useRef<HTMLDivElement>(null)
  const [state, setState] = useState<TooltipStateInterface>({
    isVisible: false,
    top: 0,
    left: 0,
    arrowLeft: '50%',
  })
  const handleHideMessage = () => setState({ ...state, isVisible: false })

  const setMessagePositionAndDisplay = (isVisible: boolean) => {
    if (!tooltipIcon?.current || !tooltipMessage?.current) {
      return false
    }

    const iconRect = tooltipIcon.current.getBoundingClientRect()
    const messageRect = tooltipMessage.current.getBoundingClientRect()

    const top = iconRect.y - messageRect.height - iconRect.height / 2

    const tooltipWidth = messageRect.width
    const viewportWidth = window.innerWidth

    let left = iconRect.x + iconRect.width / 2 - tooltipWidth / 2
    if (left < defaultMinLeft) {
      left = defaultMinLeft
    }
    const maxLeft = viewportWidth - tooltipWidth - defaultMinLeft
    if (left > maxLeft) {
      left = maxLeft
    }

    const iconCenterX = iconRect.x + iconRect.width / 2
    const tooltipLeft = left

    let rawArrowLeft = iconCenterX - tooltipLeft
    rawArrowLeft = Math.max(0, Math.min(tooltipWidth, rawArrowLeft))

    const arrowLeft = isArrowCenter
      ? `${defaultMessageMaxWidth / 2}px`
      : `${rawArrowLeft}px`

    setState({
      isVisible,
      top,
      left,
      arrowLeft,
    })
  }

  const handleShowMessage = () => {
    setMessagePositionAndDisplay(true)
  }

  const getSizePx = (): string => {
    switch (size) {
      case 'small':
        return '16px'
      case 'medium':
        return '24px'
      case 'large':
        return '32px'
      default:
        return `${size}px`
    }
  }

  useEffect(() => {
    setMessagePositionAndDisplay(false)
  }, [])

  return (
    <TooltipWrapperUi>
      {children}
      <TooltipMessageUi
        ref={tooltipMessage}
        top={state.top}
        left={state.left}
        isVisible={state.isVisible}
        maxWidth={defaultMessageMaxWidth}
        styles={{ color: messageColor }}
      >
        {skipTranslation ? message : t(message)}
        <TooltipArrowUi left={state.arrowLeft}>
          <TippyIcon />
        </TooltipArrowUi>
      </TooltipMessageUi>
      <TooltipButtonUi
        ref={tooltipIcon}
        isVisible={state.isVisible}
        onClick={handleShowMessage}
        size={getSizePx()}
        type="button"
        bgColor={buttonColor}
        bgColorActive={buttonColorActive}
        style={btnStyles}
      >
        <HintIcon width={getSizePx()} height={getSizePx()} />
      </TooltipButtonUi>
      {state.isVisible && <TooltipOverlayUi onClick={handleHideMessage} />}
    </TooltipWrapperUi>
  )
}
